import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { StyledModal } from '@shoflo/polymer/components/ErrorBoundary/LargeError.styled';
import { GenericCameraSourceSettings } from '@shoflo/polymer/components/SourceSettings/GenericCameraSourceSettings/GenericCameraSourceSettings';
import { AgoraCameraPreview } from '@shoflo/polymer/components/Join/AgoraCameraPreview/AgoraCameraPreview';
import setTransformedVideoInputs from '@shoflo/studio-redux/actions/meeting/setTransformedVideoInputs';
import setVideoInputDevice from '@shoflo/studio-redux/actions/meeting/setVideoInputDevice';
import uploadUserVirtualBackground from '@shoflo/studio-redux/actions/user/uploadUserVirtualBackground';
import saveUserVirtualBackground from '@shoflo/studio-redux/actions/user/saveUserVirtualBackground';
import { GENERIC_AUDIO_VIDEO_SETTINGS_FORM } from '@studio/constants/ModalTypes';

import knockAction from 'actions/Broadcast/knock';
import notifyDirectorAttendeeLeaving from 'actions/Broadcast/notifyDirectorAttendeeLeaving';
import { clearModal } from 'actions';
import useIsMobile from 'hooks/useIsMobile';
import { GENERIC_UPDATE_SETTINGS_MODAL } from 'constants/ModalTypes';
import { useGlobalUnload } from 'hooks/useWindowEvent';
import setUserBackground from '@shoflo/studio-redux/actions/backgroundFilters/setUserBackground';
import addToAttendeeTiles from '@shoflo/studio-redux/actions/meeting/addToAttendeeTiles';
import setGenericAudioInputDevice from '@shoflo/studio-redux/actions/meeting/setGenericAudioInputDevice';
import showNotification from '@shoflo/studio-redux/actions/notification/showNotification';
import { PASSED } from '@studio/constants/ReadinessCheck';
import { VENDOR_AGORA } from '@studio/constants/Vendor';
import { RESET_USER_VIRTUAL_BACKGROUND } from '@studio/constants/VirtualBackground';
import updateSource from 'actions/source/updateSource';
import { shouldJoinWithoutKnock } from 'utils/broadcastPresenterHelper';
import { localAttendeeSource } from 'selectors/localAttendeeSource';
import {
  clearVirtualBackground as agoraClearVirtualBackground,
  getVideoInput as agoraGetVideoInput,
  initializeVirtualBackground as agoraInitializeVirtualBackground
} from '@cvent/studio-agora/virtualBackgrounds/virtualBackground';
import {
  changeVideoInputDevice,
  changeUserBackground,
  toggleMirrorVideo
} from '@cvent/studio-agora/video/videoHelpers';
import { switchAudioInput as agoraSwitchAudioInput } from '@cvent/studio-agora/audio/audioHelper';
import { updateAgoraVideoDeviceListeners } from '@shoflo/studio-redux/utils/updateAgoraVideoDeviceListeners';

const VENDOR_CONFIG_MAP = {
  [VENDOR_AGORA]: {
    cameraPreviewElement: AgoraCameraPreview,
    clearVirtualBackground: agoraClearVirtualBackground,
    initializeBackgroundFilter: agoraInitializeVirtualBackground,
    getVideoInput: agoraGetVideoInput,
    switchAudioInput: agoraSwitchAudioInput
  }
};

const SettingsModal = () => {
  const attendeeId = useSelector(state => state.meeting.attendeeId);
  const attendeeTiles = useSelector(state => state.meeting.attendeeTiles);
  const client = useSelector(state => state.meeting.client, shallowEqual);
  const modal = useSelector(state => state.modal);
  const knock = useSelector(state => state.knock);
  const user = useSelector(state => state.user, shallowEqual);
  const audioInputDevice = useSelector(state => state.audioInputDevice);
  const audioOutputDevice = useSelector(state => state.audioOutputDevice);
  const videoInputDevice = useSelector(state => state.videoInputDevice);
  const userBackground = useSelector(state => state.userBackground);
  const transformedVideoInputs = useSelector(
    state => state.transformedVideoInputs
  );
  const cameraStatus = useSelector(state => state.readinessCheck.camera);
  const localSource = useSelector(localAttendeeSource, shallowEqual);

  const vendor = modal?.initialValue?.vendor;
  const vendorConfig = VENDOR_CONFIG_MAP[vendor] ?? {};

  const joinWithoutKnockEnabled = shouldJoinWithoutKnock();

  const isCameraAvailable = cameraStatus === PASSED;

  const isMobile = useIsMobile();

  let isOpen = false;

  if (modal) {
    isOpen = [
      GENERIC_UPDATE_SETTINGS_MODAL,
      GENERIC_AUDIO_VIDEO_SETTINGS_FORM
    ].includes(modal.type);
  }

  const dispatch = useDispatch();

  const currentVideoTrack = attendeeTiles[attendeeId];

  const [presenterTitle, setPresenterTitle] = useState('');
  const [presenterDescription, setPresenterDescription] = useState('');

  const handlePageReloadOrClose = useCallback(() => {
    dispatch(notifyDirectorAttendeeLeaving({ closedOrReloadTab: true }, true));
  }, [dispatch]);

  const onCancel = () => {
    dispatch(clearModal(modal.type));
  };

  useGlobalUnload(handlePageReloadOrClose);
  useEffect(() => {
    if (knock) {
      setPresenterTitle(knock?.name);
      setPresenterDescription(knock?.description);
    }
  }, [knock]);

  useEffect(() => {
    if (localSource && joinWithoutKnockEnabled) {
      setPresenterTitle(localSource.title);
      setPresenterDescription(localSource.description);
    }
  }, [localSource, joinWithoutKnockEnabled]);

  const onSubmit = data => {
    const { audioInputDeviceId, title, description } = data;

    dispatch(setGenericAudioInputDevice(audioInputDeviceId));

    if (localSource && joinWithoutKnockEnabled) {
      dispatch(updateSource(localSource._id, { title, description }));
    }

    if (!joinWithoutKnockEnabled) {
      dispatch(
        knockAction({
          presenterName: title,
          isTransient: false,
          presenterDescription: description || '',
          presenterDetailsUpdated: true,
          presenterPhoto: user?.photo
        })
      );
    }

    dispatch(clearModal(modal.type));
  };

  const style = { maxWidth: '800px' };

  const handleSaveUserVirtualBackground = (fileName, relativePath) =>
    dispatch(saveUserVirtualBackground(fileName, relativePath));
  const handleUploadUserVirtualBackground = file =>
    dispatch(uploadUserVirtualBackground(file));
  const handleResetUserVirtualBackground = payload =>
    dispatch({ type: RESET_USER_VIRTUAL_BACKGROUND, payload });

  const updateVideoInput = videoInput => {
    videoInput.videoTrack.userBackground = videoInput.virtualBackground;
    dispatch(addToAttendeeTiles(attendeeId, videoInput.videoTrack));
    dispatch(setUserBackground(videoInput.videoTrack.userBackground));
    dispatch(setTransformedVideoInputs([videoInput]));
    dispatch(setVideoInputDevice(videoInput));
    updateAgoraVideoDeviceListeners(videoInput, dispatch, attendeeId, client);
  };

  const {
    cameraPreviewElement,
    clearVirtualBackground,
    initializeBackgroundFilter,
    getVideoInput,
    switchAudioInput
  } = vendorConfig;

  const deviceActions = {
    addToAttendeeTiles,
    handleVideoInputChanged: setVideoInputDevice,
    handleAudioInputChanged: setGenericAudioInputDevice,
    showNotification
  };

  if (!isOpen) return null;

  return (
    <StyledModal
      isOpen={isOpen}
      fade={!isMobile}
      style={style}
      data-testId="speaker-settings"
    >
      <GenericCameraSourceSettings
        title={presenterTitle}
        description={presenterDescription}
        onSubmit={onSubmit}
        onCancel={onCancel}
        audioInputDeviceId={audioInputDevice}
        audioOutputDeviceId={audioOutputDevice}
        videoInputDeviceId={videoInputDevice?.device ?? videoInputDevice}
        videoInput={videoInputDevice}
        isCameraAvailable={isCameraAvailable}
        userBackground={userBackground}
        transformedVideoInputs={transformedVideoInputs}
        uploadUserVirtualBackground={handleUploadUserVirtualBackground}
        resetUserVirtualBackground={handleResetUserVirtualBackground}
        saveUserVirtualBackground={handleSaveUserVirtualBackground}
        user={user}
        CameraPreviewElement={cameraPreviewElement}
        clearVirtualBackground={clearVirtualBackground}
        initializeBackgroundFilter={initializeBackgroundFilter}
        getVideoInput={getVideoInput}
        switchAudioInput={switchAudioInput}
        currentVideoTrack={currentVideoTrack}
        client={client}
        dispatch={dispatch}
        deviceActions={deviceActions}
        changeVideoInputDevice={changeVideoInputDevice}
        updateVideoInput={updateVideoInput}
        changeUserBackground={changeUserBackground}
        toggleMirrorVideo={toggleMirrorVideo}
      />
    </StyledModal>
  );
};

export default SettingsModal;
